// Pieces the agency console pages share (M5-05): the week picker, the coverage notice, money
// that reads as a loss when negative, the break-glass state badge and scope, and the reason
// dialog behind Reject.
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldDescription,
  FieldLabel,
  NativeSelect,
  NativeSelectOption,
  Notice,
  Textarea,
} from "@companyos/ui";
import { type FormEvent, useId, useMemo, useState } from "react";
import { recentWeeks } from "../../focus/focus";
import { weekRangeLabel } from "../../focus/weeks";
import { formatDateTime, formatMoney } from "../format";
import type { BreakGlassSession } from "./agency-queries";

export function WeekSelect({ week, onWeek }: { week: string; onWeek: (week: string) => void }) {
  const weeks = useMemo(() => {
    const list = recentWeeks(new Date(), 12);
    return list.includes(week) ? list : [week, ...list];
  }, [week]);
  return (
    <NativeSelect
      size="sm"
      aria-label="Week"
      value={week}
      onChange={(e) => onWeek(e.target.value)}
      className="w-auto"
    >
      {weeks.map((w) => (
        <NativeSelectOption key={w} value={w}>
          {w} · {weekRangeLabel(w)}
        </NativeSelectOption>
      ))}
    </NativeSelect>
  );
}

/** "Could not read every company in full" with the server's reasons. */
export function CoverageNotice({ complete, reasons }: { complete: boolean; reasons: string[] }) {
  if (complete) return null;
  return (
    <Notice tone="warning" className="mb-4" title="Incomplete data" data-slot="coverage-incomplete">
      Some companies could not be read in full, so these numbers are partial:{" "}
      {reasons.join("; ") || "no reason given"}.
    </Notice>
  );
}

/** Minor units in the row's currency; a negative margin reads as a loss. */
export function Money({
  minor,
  currency,
  loss = false,
}: {
  minor: number;
  currency: string;
  loss?: boolean;
}) {
  const text = formatMoney(minor, currency);
  if (loss && minor < 0)
    return (
      <span className="font-medium text-danger-text" data-loss="true">
        {text}
        <span className="sr-only"> (loss)</span>
      </span>
    );
  return <>{text}</>;
}

const STATE: Record<
  BreakGlassSession["state"],
  { label: string; variant: "warning" | "danger" | "success" | "neutral" | "info" }
> = {
  requested: { label: "Requested", variant: "info" },
  active: { label: "Active", variant: "warning" },
  rejected: { label: "Rejected", variant: "neutral" },
  ended: { label: "Ended", variant: "neutral" },
  expired: { label: "Expired", variant: "neutral" },
};

export function SessionStateBadge({ state }: { state: BreakGlassSession["state"] }) {
  const s = STATE[state];
  return (
    <Badge variant={s.variant} dot data-state={state}>
      {s.label}
    </Badge>
  );
}

export function scopeText(s: Pick<BreakGlassSession, "scope" | "scope_id">): string {
  if (s.scope === "company") return "Whole company";
  return `${s.scope === "space" ? "Space" : "Channel"} …${(s.scope_id ?? "").slice(-6)}`;
}

export const durationText = (minutes: number) =>
  minutes < 60
    ? `${minutes} min`
    : minutes % 60
      ? `${(minutes / 60).toFixed(1)} h`
      : `${minutes / 60} h`;

/** Requested, started, expires and ended times, one per line (only those that happened). */
export function SessionTimes({ s }: { s: BreakGlassSession }) {
  const rows: [string, string | null][] = [
    ["Requested", s.requested_at],
    ["Started", s.starts_at],
    [
      s.state === "active" ? "Expires" : "Was due to end",
      s.state === "active" || s.ended_at ? s.expires_at : null,
    ],
    ["Ended", s.ended_at],
  ];
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-2 text-xs">
      {rows
        .filter(([, v]) => v)
        .map(([label, v]) => (
          <div key={label} className="contents">
            <dt className="text-muted-foreground">{label}</dt>
            <dd className="tabular-nums">{formatDateTime(v ?? "")}</dd>
          </div>
        ))}
    </dl>
  );
}

/** Who decided and why (a rejection's reason, an end's reason). */
export function DecisionText({ s }: { s: BreakGlassSession }) {
  if (!s.decided_by_name && !s.ended_by_name)
    return <span className="text-muted-foreground">Waiting</span>;
  return (
    <div className="flex flex-col gap-0.5 text-sm">
      {s.decided_by_name ? (
        <span>
          {s.state === "rejected" ? "Rejected" : "Approved"} by {s.decided_by_name}
          <span className="text-muted-foreground">
            {" "}
            ({s.decided_by_kind === "company_owner" ? "company owner" : "agency admin"})
          </span>
        </span>
      ) : null}
      {s.reject_reason ? <span className="text-muted-foreground">“{s.reject_reason}”</span> : null}
      {s.ended_by_name ? (
        <span>
          Ended by {s.ended_by_name}
          {s.end_reason ? <span className="text-muted-foreground"> · “{s.end_reason}”</span> : null}
        </span>
      ) : null}
    </div>
  );
}

/** Reject with a reason (required by the API). */
export function RejectDialog({
  open,
  onOpenChange,
  who,
  pending,
  onReject,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  who: string;
  pending: boolean;
  onReject: (reason: string) => void;
}) {
  const [reason, setReason] = useState("");
  const id = useId();
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (reason.trim()) onReject(reason.trim());
  };
  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) setReason("");
        onOpenChange(o);
      }}
    >
      <DialogContent className="sm:max-w-md">
        <form onSubmit={submit} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>Reject the request</DialogTitle>
            <DialogDescription>
              {who} is told why. The request is closed; they can ask again.
            </DialogDescription>
          </DialogHeader>
          <Field>
            <FieldLabel htmlFor={id}>Reason</FieldLabel>
            <Textarea
              id={id}
              rows={3}
              maxLength={2000}
              required
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
            <FieldDescription>Recorded on the session and in the audit log.</FieldDescription>
          </Field>
          <DialogFooter>
            <Button type="submit" variant="destructive" disabled={pending || !reason.trim()}>
              {pending ? "Rejecting…" : "Reject request"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
